import { navigateBackOrFallback } from "../../shared/screen-shell/screen-navigation.utils";
import { AuthenticatedRealtimeService } from "../../shared/services/authenticated-realtime.service";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { BehaviorSubject, Observable, Subject, forkJoin, EMPTY } from "rxjs";
import { map, switchMap, takeUntil, catchError } from "rxjs/operators";
import { Event, EventPlayerBlock, InactiveEventPlayer, RemoveInactivePlayersResult } from "../../shared/models/event";
import { Player, PlayerType } from "../../shared/models/player";
import { GuestParticipationRequest, PlayerEventInfo, PlayerEventInfoStatus, TeamMemberType } from "../../shared/models/playerEventInfo";
import { EventService } from "../../shared/services/event.service";
import { PlayerEventInfoService } from "../../shared/services/playereventinfo.service";
import { PlayerService } from "../../shared/services/player.service";
import { TranslateService } from "@ngx-translate/core";
import { nogoshIconClass } from "../../shared/icons/nogosh-icons.generated";

type PlayerManagementTab = "massUpdate" | "stalePlayers" | "blockedPlayers";

@Component({
    selector: "event-player-management",
    templateUrl: "./event-player-management.component.html",
    styleUrls: ["../event.component.css"],
    standalone: false
})
export class EventPlayerManagementComponent implements OnInit, OnDestroy {
  private stopRealtime: () => void;
  private readonly destroy$ = new Subject<void>();
  private readonly refresh$ = new Subject<void>();
  @Input() public eventId: string = null;
  @Input() public embedded: boolean = false;
  @Input() public initialEvent: Event = null;
  @Input() public initialPlayers: PlayerEventInfo[] = null;
  @Input() public initialBlockedPlayers: EventPlayerBlock[] = null;
  @Output() public playerListChanged = new EventEmitter<PlayerEventInfo[]>();
  @Output() public playerSelected = new EventEmitter<PlayerEventInfo>();
  @Output() public closed = new EventEmitter<void>();

  public event: Event;
  public currentPlayer: Player;
  public playerEventInfos: PlayerEventInfo[] = [];
  public filteredPlayerEventInfos: PlayerEventInfo[] = [];
  public loading: boolean = false;
  public readonly playerManagementReady$ = new BehaviorSubject<boolean>(false);
  public actionInProgress: boolean = false;
  public errorMsg: string = null;
  public successMsg: string = null;
  public warningMsg: string = null;
  public activeManagementTab: PlayerManagementTab = "massUpdate";
  public statusFilter: string = "ALL";
  public memberTypeFilter: string = "ALL";
  public nickNameFilter: string = "";
  public selectedPlayerEventInfoIds: {[id: string]: boolean} = {};
  public showRemoveConfirm: boolean = false;
  public cleanupMonths: number = 3;
  public cleanupMonthOptions: number[] = [1, 2, 3, 6, 12];
  public cleanupPlayers: InactiveEventPlayer[] = [];
  public selectedCleanupPlayerIds: {[playerId: string]: boolean} = {};
  public cleanupLoading: boolean = false;
  public cleanupSearched: boolean = false;
  public cleanupConfirm: boolean = false;
  public cleanupErrorMsg: string = null;
  public playerEventInfoPendingRemoval: PlayerEventInfo = null;
  public blockedPlayers: EventPlayerBlock[] = [];
  public blockedPlayersLoading: boolean = false;

  public playerEventInfoStatus: PlayerEventInfoStatus = new PlayerEventInfoStatus();
  public teamMemberType: TeamMemberType = new TeamMemberType();
  public playerType: PlayerType = new PlayerType();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private eventService: EventService,
    private playerEventInfoService: PlayerEventInfoService,
    private playerService: PlayerService,
    private translate: TranslateService,
    private realtime: AuthenticatedRealtimeService
  ) {}

  ngOnInit(): void {
    this.currentPlayer = this.playerService.getCurrentPlayer();
    const eventId = this.eventId || this.route.snapshot.params["eventId"];
    if (!eventId) {
      this.router.navigate(["/event-list"]);
      return;
    }

    this.refresh$.pipe(switchMap(() => forkJoin({
      players: this.playerEventInfoService.fetchForEvent(eventId),
      blocked: this.eventService.getBlockedPlayers(eventId)
    }).pipe(catchError(error => { this.errorMsg = this.getErrorMessage(error); return EMPTY; }))), takeUntil(this.destroy$)).subscribe({next: ({players, blocked}) => {
      this.playerEventInfos = players || [];
      this.blockedPlayers = blocked || [];
      this.applyFilters();
      this.playerListChanged.emit(this.playerEventInfos);
    }, error: error => { this.errorMsg = this.getErrorMessage(error); }});
    this.stopRealtime = this.realtime.subscribe(({type, data}) => {
      if (type === 'connected' || (data?.eventId === eventId && ['participants.changed', 'event.updated', 'event.deleted'].includes(type))) this.refresh$.next();
    });
    if (this.initialEvent && this.initialPlayers && this.initialBlockedPlayers) {
      this.event = this.initialEvent;
      this.playerEventInfos = this.initialPlayers;
      this.blockedPlayers = this.initialBlockedPlayers;
      this.applyFilters();
      this.playerManagementReady$.next(true);
      return;
    }
    this.loadEvent(eventId);
  }

  ngOnDestroy(): void {
    this.stopRealtime?.();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadEvent(eventId: string): void {
    this.loading = true;
    this.playerManagementReady$.next(false);
    this.errorMsg = null;

    this.eventService.getEvent(eventId).subscribe(
      (event) => {
        this.event = event;
this.event.viewedByManager = !!event.manager && !!this.currentPlayer && event.manager.id === this.currentPlayer.id;

if (!this.event.viewedByManager) {
  this.errorMsg = this.translate.instant('playerManagement.errors.managerOnly');
  this.loading = false;
  this.playerManagementReady$.next(true);
  return;
}

this.loadPlayers();
this.loadBlockedPlayers();
      },
      (error) => {
        this.errorMsg = this.getErrorMessage(error);
this.loading = false;
this.playerManagementReady$.next(true);
      }
    );
  }

  public loadPlayers(): void {
    if (!this.event) {
      return;
    }

    this.loading = true;
    this.playerEventInfoService.fetchForEvent(this.event.id).subscribe(
      (playerEventInfos) => {
        this.playerEventInfos = playerEventInfos || [];
this.selectedPlayerEventInfoIds = {};
this.applyFilters();
this.loading = false;
this.playerManagementReady$.next(true);
      },
      (error) => {
        this.errorMsg = this.getErrorMessage(error);
this.loading = false;
this.playerManagementReady$.next(true);
      }
    );
  }

  public applyFilters(): void {
    const nickNameFilter = (this.nickNameFilter || "").trim().toLowerCase();
    this.filteredPlayerEventInfos = (this.playerEventInfos || []).filter((playerEventInfo) => {
      const statusMatches = this.statusFilter === "ALL" || playerEventInfo.status === this.statusFilter;
      const memberTypeMatches = this.memberTypeFilter === "ALL" || playerEventInfo.teamMemberType === this.memberTypeFilter;
      const playerNickName = playerEventInfo.player && playerEventInfo.player.nickName ? playerEventInfo.player.nickName.toLowerCase() : "";
      const nickNameMatches = !nickNameFilter || playerNickName.indexOf(nickNameFilter) > -1;
      return statusMatches && memberTypeMatches && nickNameMatches;
    });

    this.selectedPlayerEventInfoIds = Object.keys(this.selectedPlayerEventInfoIds).reduce((selection, id) => {
      if (this.filteredPlayerEventInfos.some((playerEventInfo) => playerEventInfo.id === id)) {
        selection[id] = this.selectedPlayerEventInfoIds[id];
      }
      return selection;
    }, {});
  }

  public updateStatus(playerEventInfo: PlayerEventInfo, status: string): void {
    if (!playerEventInfo || playerEventInfo.status === status || this.actionInProgress) {
      return;
    }

    this.updateParticipation(Object.assign(new PlayerEventInfo(), playerEventInfo, {status}));
  }

  public updateTeamMemberType(playerEventInfo: PlayerEventInfo, teamMemberType: string): void {
    if (!playerEventInfo || playerEventInfo.teamMemberType === teamMemberType || this.actionInProgress) {
      return;
    }

    this.updateParticipation(Object.assign(new PlayerEventInfo(), playerEventInfo, {teamMemberType}));
  }

  public confirmRemovePlayer(playerEventInfo: PlayerEventInfo): void {
    if (!playerEventInfo || this.actionInProgress) {
      return;
    }

    this.playerEventInfoPendingRemoval = playerEventInfo;
  }

  public cancelRemovePlayer(): void {
    if (this.actionInProgress) {
      return;
    }

    this.playerEventInfoPendingRemoval = null;
  }

  public removePlayer(playerEventInfo: PlayerEventInfo): void {
    if (!playerEventInfo || this.actionInProgress) {
      return;
    }

    this.actionInProgress = true;
    this.errorMsg = null;
    this.successMsg = null;

    this.removeParticipation(playerEventInfo).subscribe(
      () => {
        this.playerEventInfos = this.playerEventInfos.filter((item) => item.id !== playerEventInfo.id);
this.applyFilters();
this.actionInProgress = false;
this.playerEventInfoPendingRemoval = null;
this.playerListChanged.emit([...this.playerEventInfos]);
this.successMsg = this.translate.instant('playerManagement.messages.playerRemoved');
      },
      (error) => {
        this.errorMsg = this.getErrorMessage(error);
this.actionInProgress = false;
      }
    );
  }

  public blockPlayer(playerEventInfo: PlayerEventInfo): void {
    if (!playerEventInfo || !playerEventInfo.player || this.actionInProgress || this.isGuestPlayer(playerEventInfo.player)) return;
    this.actionInProgress = true;
    this.errorMsg = null;
    this.eventService.blockPlayer(this.event.id, playerEventInfo.player.id).subscribe(
      (blockedPlayer) => {
        this.playerEventInfos = this.playerEventInfos.filter((item) => item.id !== playerEventInfo.id);
        this.blockedPlayers = [blockedPlayer].concat(this.blockedPlayers.filter((item) => item.playerId !== blockedPlayer.playerId));
        this.applyFilters();
        this.actionInProgress = false;
        this.playerEventInfoPendingRemoval = null;
        this.playerListChanged.emit([...this.playerEventInfos]);
        this.successMsg = this.translate.instant('playerManagement.blocked.playerBlocked');
      },
      (error) => {
        this.errorMsg = this.getErrorMessage(error);
        this.actionInProgress = false;
      }
    );
  }

  public selectManagementTab(tab: PlayerManagementTab): void {
    this.activeManagementTab = tab;
    if (tab === "blockedPlayers") this.loadBlockedPlayers();
  }

  public loadBlockedPlayers(): void {
    if (!this.event || this.blockedPlayersLoading) return;
    this.blockedPlayersLoading = true;
    this.eventService.getBlockedPlayers(this.event.id).subscribe(
      (players) => {
        this.blockedPlayers = players || [];
        if (!this.blockedPlayers.length && this.activeManagementTab === "blockedPlayers") this.activeManagementTab = "massUpdate";
        this.blockedPlayersLoading = false;
      },
      (error) => {
        this.errorMsg = this.getErrorMessage(error);
        this.blockedPlayersLoading = false;
      }
    );
  }

  public unblockPlayer(blockedPlayer: EventPlayerBlock): void {
    if (!blockedPlayer || this.actionInProgress) return;
    this.actionInProgress = true;
    this.eventService.unblockPlayer(this.event.id, blockedPlayer.playerId).subscribe(
      () => {
        this.blockedPlayers = this.blockedPlayers.filter((item) => item.playerId !== blockedPlayer.playerId);
        if (!this.blockedPlayers.length && this.activeManagementTab === "blockedPlayers") this.activeManagementTab = "massUpdate";
        this.actionInProgress = false;
        this.successMsg = this.translate.instant('playerManagement.blocked.playerUnblocked');
      },
      (error) => {
        this.errorMsg = this.getErrorMessage(error);
        this.actionInProgress = false;
      }
    );
  }

  public getSelectedPlayerEventInfos(): PlayerEventInfo[] {
    return this.playerEventInfos.filter((playerEventInfo) => this.selectedPlayerEventInfoIds[playerEventInfo.id]);
  }

  public getSelectedVisiblePlayerEventInfos(): PlayerEventInfo[] {
    return this.filteredPlayerEventInfos.filter((playerEventInfo) => this.selectedPlayerEventInfoIds[playerEventInfo.id]);
  }

  public areAllVisiblePlayersSelected(): boolean {
    return this.filteredPlayerEventInfos.length > 0 &&
      this.filteredPlayerEventInfos.every((playerEventInfo) => this.selectedPlayerEventInfoIds[playerEventInfo.id]);
  }

  public toggleVisiblePlayersSelection(checked: boolean): void {
    this.filteredPlayerEventInfos.forEach((playerEventInfo) => {
      this.selectedPlayerEventInfoIds[playerEventInfo.id] = checked;
    });
  }

  public removeSelectedPlayers(): void {
    const selectedPlayers = this.getSelectedPlayerEventInfos();
    if (selectedPlayers.length === 0 || this.actionInProgress) {
      return;
    }

    this.actionInProgress = true;
    this.errorMsg = null;
    this.successMsg = null;

    this.removeNextSelectedPlayer(selectedPlayers, 0, 0);
  }

  private removeNextSelectedPlayer(players: PlayerEventInfo[], index: number, removedCount: number): void {
    if (index >= players.length) {
      this.actionInProgress = false;
this.showRemoveConfirm = false;
this.successMsg = this.translate.instant('playerManagement.messages.playersRemoved', { count: removedCount });
this.selectedPlayerEventInfoIds = {};
this.playerListChanged.emit([...this.playerEventInfos]);
this.loadPlayers();
      return;
    }

    this.removeParticipation(players[index]).subscribe(
      () => this.removeNextSelectedPlayer(players, index + 1, removedCount + 1),
      (error) => {
        this.warningMsg = this.translate.instant('playerManagement.messages.removePartialFailed', { error: this.getErrorMessage(error) });
this.removeNextSelectedPlayer(players, index + 1, removedCount);
      }
    );
  }

  public bulkUpdateSelectedStatus(status: string): void {
    const selectedPlayers = this.getSelectedVisiblePlayerEventInfos()
      .filter((playerEventInfo) => playerEventInfo.status !== status);
    this.bulkUpdateSelectedPlayers(selectedPlayers, 0, status, null, 0);
  }

  public bulkUpdateSelectedTeamMemberType(teamMemberType: string): void {
    const selectedPlayers = this.getSelectedVisiblePlayerEventInfos()
      .filter((playerEventInfo) => playerEventInfo.teamMemberType !== teamMemberType);
    this.bulkUpdateSelectedPlayers(selectedPlayers, 0, null, teamMemberType, 0);
  }

  private bulkUpdateSelectedPlayers(players: PlayerEventInfo[], index: number, status: string, teamMemberType: string, updatedCount: number): void {
    if (players.length === 0 || (index === 0 && this.actionInProgress)) {
      return;
    }

    if (index === 0) {
      this.actionInProgress = true;
      this.errorMsg = null;
      this.successMsg = null;
      this.warningMsg = null;
    }

    if (index >= players.length) {
      this.actionInProgress = false;
this.successMsg = this.translate.instant('playerManagement.messages.playersUpdated', { count: updatedCount });
this.playerListChanged.emit([...this.playerEventInfos]);
      return;
    }

    const playerEventInfo = players[index];
    if (status) {
      playerEventInfo.status = status;
    }
    if (teamMemberType) {
      playerEventInfo.teamMemberType = teamMemberType;
    }

    this.updateParticipationRequest(playerEventInfo).subscribe(
      (updatedPlayerEventInfo) => {
        this.replacePlayerEventInfo(updatedPlayerEventInfo);
this.bulkUpdateSelectedPlayers(players, index + 1, status, teamMemberType, updatedCount + 1);
      },
      (error) => {
        this.warningMsg = this.translate.instant('playerManagement.messages.updatePartialFailed', { error: this.getErrorMessage(error) });
this.bulkUpdateSelectedPlayers(players, index + 1, status, teamMemberType, updatedCount);
      }
    );
  }

  public loadCleanupPlayers(): void {
    if (!this.event || this.cleanupLoading) {
      return;
    }

    this.cleanupLoading = true;
    this.cleanupSearched = false;
    this.cleanupErrorMsg = null;
    this.warningMsg = null;
    this.successMsg = null;
    this.cleanupConfirm = false;

    this.eventService.getInactivePlayersForCleanup(this.event.id, this.cleanupMonths).subscribe(
      (players) => {
        this.cleanupPlayers = players || [];
        this.cleanupSearched = true;
this.selectedCleanupPlayerIds = {};
for (let player of this.cleanupPlayers) {
  this.selectedCleanupPlayerIds[player.playerId] = true;
}
this.cleanupLoading = false;
      },
      (error) => {
        this.cleanupErrorMsg = this.getCleanupErrorMessage(error);
this.cleanupPlayers = [];
this.selectedCleanupPlayerIds = {};
this.cleanupLoading = false;
      }
    );
  }

  public getSelectedCleanupPlayerIds(): string[] {
    return Object.keys(this.selectedCleanupPlayerIds).filter((playerId) => this.selectedCleanupPlayerIds[playerId]);
  }

  public removeCleanupPlayers(): void {
    const playerIds = this.getSelectedCleanupPlayerIds();
    if (!this.event || playerIds.length === 0 || this.actionInProgress) {
      return;
    }

    this.actionInProgress = true;
    this.cleanupErrorMsg = null;
    this.warningMsg = null;
    this.successMsg = null;

    this.eventService.removeInactivePlayers(this.event.id, playerIds).subscribe(
      (result) => this.handleCleanupResult(result),
      (error) => {
        this.cleanupErrorMsg = this.getCleanupErrorMessage(error);
this.actionInProgress = false;
      }
    );
  }

  private handleCleanupResult(result: RemoveInactivePlayersResult): void {
    const missingPlayerIds = result && result.missingPlayerIds ? result.missingPlayerIds : [];
    const removedPlayerIds = result && result.removedPlayerIds ? result.removedPlayerIds : [];
    const removedCount = result && result.removedCount ? result.removedCount : 0;

    if (missingPlayerIds.length > 0) {
      this.warningMsg = this.translate.instant('playerManagement.messages.playersMissing', { ids: missingPlayerIds.join(", ") });
    }

    this.successMsg = this.translate.instant('playerManagement.messages.playersRemoved', { count: removedCount });
    this.cleanupConfirm = false;
    this.cleanupPlayers = this.cleanupPlayers.filter((player) => removedPlayerIds.indexOf(player.playerId) === -1);
    this.selectedCleanupPlayerIds = {};
    for (let player of this.cleanupPlayers) {
      this.selectedCleanupPlayerIds[player.playerId] = true;
    }
    this.actionInProgress = false;
    this.playerListChanged.emit([...this.playerEventInfos]);
    this.loadPlayers();
  }

  public goBack(): void {
    if (this.embedded) {
      this.closed.emit();
      return;
    }

    navigateBackOrFallback(this.location, this.router, ["/event-list"]);
  }

  public isGuestPlayer(player: Player): boolean {
    return !!player && (
      player.guest === true ||
      player.playerType === this.playerType.MANAGED_PLAYER ||
      player.playerType === this.playerType.GUEST
    );
  }

  public playerManagementLabel(playerEventInfo: PlayerEventInfo): string {
    if (!playerEventInfo) {
      return "";
    }

    if (this.isGuestPlayer(playerEventInfo.player)) {
      const labelKey = playerEventInfo.player.playerType === this.playerType.MANAGED_PLAYER || playerEventInfo.player.managedPlayer === true
        ? 'guests.managedPlayer'
        : 'guests.guest';
      return this.translate.instant(labelKey);
    }

    return "";
  }

  public statusOptions(): string[] {
    return [
      this.playerEventInfoStatus.CONFIRMED,
      this.playerEventInfoStatus.NOT_ANSWERED,
      this.playerEventInfoStatus.DECLINED,
      this.playerEventInfoStatus.NOT_NOTIFIED
    ];
  }

  public teamMemberOptions(): string[] {
    return [
      this.teamMemberType.FIRST_TEAM,
      this.teamMemberType.RESERVE,
      this.teamMemberType.CANDIDATE
    ];
  }

  public teamMemberOptionsForPlayer(playerEventInfo: PlayerEventInfo): string[] {
    const visibleOptions = [
      this.teamMemberType.FIRST_TEAM,
      this.teamMemberType.RESERVE
    ];

    if (playerEventInfo && playerEventInfo.teamMemberType === this.teamMemberType.CANDIDATE) {
      visibleOptions.push(this.teamMemberType.CANDIDATE);
    }

    return visibleOptions;
  }

  public displayStatusOption(status: string): string {
    if (status === "ALL") {
      return this.translate.instant('playerManagement.allStatuses');
    }
    if (status === this.playerEventInfoStatus.CONFIRMED) {
      return this.translate.instant('guestOwner.coming');
    }
    if (status === this.playerEventInfoStatus.NOT_ANSWERED) {
      return this.translate.instant('events.notAnswered');
    }
    if (status === this.playerEventInfoStatus.DECLINED) {
      return this.translate.instant('guestOwner.notComing');
    }
    if (status === this.playerEventInfoStatus.NOT_NOTIFIED) {
      return this.translate.instant('events.notNotified');
    }
    return status;
  }

  public displayStatusIconClass(status: string): string {
    if (status === "ALL") {
      return nogoshIconClass("common.unselected");
    }
    if (status === this.playerEventInfoStatus.CONFIRMED) {
      return nogoshIconClass("common.check");
    }
    if (status === this.playerEventInfoStatus.NOT_ANSWERED) {
      return nogoshIconClass("match.status.notAnswered");
    }
    if (status === this.playerEventInfoStatus.DECLINED) {
      return nogoshIconClass("match.status.declined");
    }
    if (status === this.playerEventInfoStatus.NOT_NOTIFIED) {
      return nogoshIconClass("common.minus");
    }
    return nogoshIconClass("common.unselected");
  }

  public displayTeamMemberOption(teamMemberType: string): string {
    if (teamMemberType === this.teamMemberType.FIRST_TEAM) {
      return this.translate.instant('notifications.firstTeam');
    }
    if (teamMemberType === this.teamMemberType.RESERVE) {
      return this.translate.instant('players.reserve');
    }
    if (teamMemberType === this.teamMemberType.CANDIDATE) {
      return this.translate.instant('players.candidate');
    }
    return teamMemberType;
  }

  private updateParticipation(playerEventInfo: PlayerEventInfo): void {
    this.actionInProgress = true;
    this.errorMsg = null;
    this.successMsg = null;

    this.updateParticipationRequest(playerEventInfo).subscribe(
      (updatedPlayerEventInfo) => {
        this.replacePlayerEventInfo(updatedPlayerEventInfo);
this.applyFilters();
this.actionInProgress = false;
this.playerListChanged.emit([...this.playerEventInfos]);
this.successMsg = this.translate.instant('playerManagement.messages.listUpdated');
      },
      (error) => {
        this.errorMsg = this.getErrorMessage(error);
this.actionInProgress = false;
this.loadPlayers();
      }
    );
  }

  private updateParticipationRequest(playerEventInfo: PlayerEventInfo): Observable<PlayerEventInfo> {
    return this.isGuestPlayer(playerEventInfo.player) ?
      this.playerService.updateGuestParticipation(playerEventInfo.player.id, this.event.id, this.buildGuestParticipationRequest(playerEventInfo)) :
      this.playerEventInfoService.update(playerEventInfo, this.currentPlayer);
  }

  private replacePlayerEventInfo(updatedPlayerEventInfo: PlayerEventInfo): void {
    const existingIndex = this.playerEventInfos.findIndex((item) => item.id === updatedPlayerEventInfo.id);
    if (existingIndex > -1) {
      this.playerEventInfos[existingIndex] = updatedPlayerEventInfo;
    }
    this.applyFilters();
  }

  private removeParticipation(playerEventInfo: PlayerEventInfo): Observable<any> {
    if (this.isGuestPlayer(playerEventInfo.player)) {
      return this.playerService.removeGuestFromEvent(playerEventInfo.player.id, this.event.id);
    }

    return this.eventService.removeInactivePlayers(this.event.id, [playerEventInfo.player.id]).pipe(
      map((response: RemoveInactivePlayersResult) => {
        const removedPlayerIds = response && response.removedPlayerIds ? response.removedPlayerIds : [];
        if (removedPlayerIds.indexOf(playerEventInfo.player.id) === -1) {
          throw new Error(this.translate.instant('playerManagement.errors.removeFailed'));
        }
        return response;
      })
    );
  }

  private buildGuestParticipationRequest(playerEventInfo: PlayerEventInfo): GuestParticipationRequest {
    return {
      status: playerEventInfo.status,
      jersey: playerEventInfo.jersey,
      teamMemberType: playerEventInfo.teamMemberType,
    };
  }

  private getCleanupErrorMessage(error: any): string {
    if (error && error.status === 403) {
      return this.translate.instant('playerManagement.errors.cleanupManagerOnly');
    }
    if (error && error.status === 404) {
      return this.translate.instant('playerManagement.errors.matchNotFound');
    }
    if (error && error.status === 400) {
      return this.translate.instant('playerManagement.errors.invalidThreshold');
    }
    return this.getErrorMessage(error);
  }

  private getErrorMessage(response: any): string {
    if (response && response.message) {
      return response.message;
    }

    if (response && response.error) {
      if (response.error.message) {
        return response.error.message;
      }
      if (response.error.errorMsg) {
        return response.error.errorMsg;
      }
      if (typeof response.error === "string") {
        return response.error;
      }
    }

    return this.translate.instant('playerManagement.errors.actionFailed');
  }
}
